import { HttpErrorResponse } from '@angular/common/http';

/** Erreur HTTP mise en mots pour l'utilisateur : ce que le serveur a dit, sinon un repli. */
export interface ErreurHttpLisible {
  statut: number;
  /** Libellé court du statut (« Erreur interne du serveur »), seul affiché hors dev avec le code. */
  libelle: string;
  /** Message principal (DRF `detail`, exception Django, message de l'erreur), ou repli générique. */
  message: string;
  /** Erreurs de champ DRF, une ligne par champ. */
  details: string[];
  methode?: string;
  url?: string;
  /**
   * Résumé à copier pour le dev (ou une IA) : requête, exception, frames du code du projet et
   * frame qui a levé. Sinon le corps de la réponse.
   */
  trace: string;
  /** Faux quand rien d'exploitable n'est venu du serveur : le message est alors le repli. */
  messageServeur: boolean;
}

const REPLIS: Record<number, string> = {
  500: 'Le serveur a rencontré une erreur inattendue en traitant la demande.',
  502: 'Le serveur est momentanément injoignable.',
  503: 'Le service est momentanément indisponible.',
  504: 'Le serveur a mis trop de temps à répondre.',
};
const LIBELLES: Record<number, string> = {
  0: 'Serveur injoignable',
  400: 'Requête invalide',
  401: 'Authentification requise',
  403: 'Accès refusé',
  404: 'Ressource introuvable',
  405: 'Méthode non autorisée',
  408: 'Délai de requête dépassé',
  409: 'Conflit',
  413: 'Contenu trop volumineux',
  422: 'Données non traitables',
  429: 'Trop de requêtes',
  500: 'Erreur interne du serveur',
  502: 'Passerelle en erreur',
  503: 'Service indisponible',
  504: 'Délai de réponse dépassé',
};
const REPLI_GENERIQUE = 'Une erreur est survenue lors de la communication avec le serveur.';
const LONGUEUR_MAX = 600;

/** Ramène une valeur d'erreur DRF (chaîne, liste, objet imbriqué) à du texte. */
function texte(valeur: unknown): string {
  if (valeur == null) return '';
  if (typeof valeur === 'string') return valeur.trim();
  if (typeof valeur === 'number' || typeof valeur === 'boolean') return String(valeur);
  if (Array.isArray(valeur)) return valeur.map(texte).filter(Boolean).join(' ');
  if (typeof valeur === 'object') {
    return Object.entries(valeur as Record<string, unknown>)
      .map(([cle, v]) => `${cle} : ${texte(v)}`)
      .join(' ; ');
  }
  return '';
}

function tronquer(message: string): string {
  return message.length > LONGUEUR_MAX ? `${message.slice(0, LONGUEUR_MAX)}…` : message;
}

/**
 * Corps HTML (page de debug Django, page d'erreur du proxy) : on n'en garde que le titre de
 * l'exception, jamais la page.
 */
function messageHtml(corps: string): string {
  try {
    const page = new DOMParser().parseFromString(corps, 'text/html');
    const valeur = page.querySelector('.exception_value')?.textContent?.trim();
    const titre = page.querySelector('title')?.textContent?.trim() ?? page.querySelector('h1')?.textContent?.trim();
    return [titre, valeur].filter(Boolean).join(' — ');
  } catch {
    return '';
  }
}

/** Traceback texte de la page de debug Django (vue « copy-and-paste »), sinon le texte de la page. */
function traceHtml(corps: string): string {
  try {
    const page = new DOMParser().parseFromString(corps, 'text/html');
    const texte = (page.querySelector('#traceback_area')?.textContent ?? page.body?.textContent ?? '').trim();
    // L'en-tête (environnement, applications, middlewares) noie la pile : on part de « Traceback ».
    const debut = texte.indexOf('Traceback');
    return debut > 0 ? texte.slice(debut) : texte;
  } catch {
    return '';
  }
}

function traceCorps(corps: unknown): string {
  if (typeof corps === 'string') return /<\/?[a-z][\s\S]*>/i.test(corps) ? traceHtml(corps) : corps.trim();
  if (corps && typeof corps === 'object' && !(corps instanceof ProgressEvent) && !(corps instanceof Blob)) {
    try {
      return JSON.stringify(corps, null, 2);
    } catch {
      return '';
    }
  }
  return '';
}

const FRAME = /^\s*File "(.+)", line (\d+), in (.+)$/;
/** Frames de bibliothèques (Django, DRF, stdlib) : masquées du résumé, sauf celle qui a levé. */
const BIBLIOTHEQUE = /[\\/](site-packages|dist-packages|venv|\.venv|lib[\\/]python[\d.]*|Lib)[\\/]/;

interface Frame { fichier: string; ligne: string; fonction: string; code: string }

/** Chemin à partir du dossier `backend` (ou du paquet), sans le préfixe de la machine. */
function cheminCourt(fichier: string): string {
  const normalise = fichier.replace(/\\/g, '/');
  const paquets = normalise.lastIndexOf('-packages/');
  if (paquets >= 0) return normalise.slice(paquets + '-packages/'.length);
  const backend = normalise.lastIndexOf('/backend/');
  if (backend >= 0) return normalise.slice(backend + '/backend/'.length);
  const stdlib = normalise.lastIndexOf('/Lib/');
  return stdlib >= 0 ? normalise.slice(stdlib + 1) : normalise;
}

/**
 * Réduit un traceback Python à l'essentiel : exception, frames du projet, frame finale. Les lignes
 * de soulignement (`^^^^`) et les frames intermédiaires des bibliothèques disparaissent.
 */
function condenserTraceback(trace: string): string {
  const lignes = trace.split(/\r?\n/);
  const frames: Frame[] = [];
  for (let i = 0; i < lignes.length; i++) {
    const m = FRAME.exec(lignes[i]);
    if (!m) continue;
    const suivante = lignes[i + 1]?.trim() ?? '';
    frames.push({ fichier: m[1], ligne: m[2], fonction: m[3], code: FRAME.test(lignes[i + 1] ?? '') || /^\^+$/.test(suivante) ? '' : suivante });
  }
  if (!frames.length) return trace;

  const type = /^Exception Type:\s*(.+?)(?: at .*)?$/m.exec(trace)?.[1]?.trim();
  const valeur = /^Exception Value:\s*([\s\S]*?)\s*$/m.exec(trace)?.[1]?.trim();
  // Hors page Django : la dernière ligne non vide d'un traceback Python est « Type: message ».
  const derniere = lignes.map(l => l.trim()).filter(Boolean).pop() ?? '';
  const exception = type ? `${type}: ${valeur ?? ''}`.trim() : derniere;
  // Exceptions chaînées : la cause d'origine est souvent la vraie piste.
  const causes = lignes.map(l => l.trim()).filter(l => /^(The above exception|During handling of the above exception)/.test(l));

  const finale = frames[frames.length - 1];
  const gardees = frames.filter(f => f === finale || !BIBLIOTHEQUE.test(f.fichier));
  const masquees = frames.length - gardees.length;
  const pile = gardees.map(f => `  ${cheminCourt(f.fichier)}:${f.ligne} in ${f.fonction}${f.code ? `\n    ${f.code}` : ''}`);
  return [
    exception,
    ...causes,
    '',
    `Traceback (code du projet${masquees ? `, ${masquees} frame${masquees > 1 ? 's' : ''} de bibliothèques masquée${masquees > 1 ? 's' : ''}` : ''}) :`,
    ...pile,
  ].join('\n');
}

function cheminRequete(url?: string | null): string {
  if (!url) return '';
  try {
    const u = new URL(url, location.origin);
    return decodeURIComponent(u.pathname + u.search);
  } catch {
    return url;
  }
}

const SECRET = /pass|token|secret|key|authorization|iban|card/i;
const PAYLOAD_MAX = 1200;

/** Corps de la requête envoyée, secrets masqués et tronqué : souvent indispensable pour reproduire. */
function payloadLisible(corps: unknown): string {
  if (corps == null || corps === '') return '';
  if (typeof FormData !== 'undefined' && corps instanceof FormData) {
    const champs: string[] = [];
    corps.forEach((valeur, cle) => champs.push(`${cle}=${SECRET.test(cle) ? '***' : valeur instanceof File ? `<fichier ${valeur.name}>` : valeur}`));
    return champs.join('\n');
  }
  if (corps instanceof Blob) return `<blob ${corps.size} octets>`;
  let texteCorps: string;
  try {
    texteCorps = typeof corps === 'string' ? corps : JSON.stringify(corps, (cle, valeur) => cle && SECRET.test(cle) ? '***' : valeur, 2);
  } catch {
    return '';
  }
  return texteCorps.length > PAYLOAD_MAX ? `${texteCorps.slice(0, PAYLOAD_MAX)}…` : texteCorps;
}

export function decrireErreurHttp(erreur: HttpErrorResponse, methode?: string, corpsRequete?: unknown): ErreurHttpLisible {
  const corps: unknown = erreur.error;
  const details: string[] = [];
  let message = '';

  if (typeof corps === 'string') {
    message = /<\/?[a-z][\s\S]*>/i.test(corps) ? messageHtml(corps) : corps.trim();
  } else if (corps && typeof corps === 'object' && !(corps instanceof ProgressEvent) && !(corps instanceof Blob)) {
    const { detail, error, message: msg, non_field_errors, ...champs } = corps as Record<string, unknown>;
    message = texte(detail) || texte(error) || texte(msg) || texte(non_field_errors);
    for (const [champ, valeur] of Object.entries(champs)) {
      const ligne = texte(valeur);
      if (ligne) details.push(`${champ} : ${ligne}`);
    }
  }

  const messageServeur = !!message || details.length > 0;
  const payload = payloadLisible(corpsRequete);
  return {
    statut: erreur.status,
    libelle: LIBELLES[erreur.status] ?? (erreur.statusText && erreur.statusText !== 'OK' ? erreur.statusText : 'Erreur'),
    message: tronquer(message || REPLIS[erreur.status] || REPLI_GENERIQUE),
    details: details.slice(0, 8).map(tronquer),
    methode,
    url: erreur.url ?? undefined,
    trace: [
      `HTTP ${erreur.status} ${LIBELLES[erreur.status] ?? erreur.statusText ?? ''}`.trim(),
      `${methode ?? ''} ${cheminRequete(erreur.url)}`.trim(),
      ...(payload ? ['', 'Corps envoyé :', payload] : []),
      '',
      condenserTraceback(traceCorps(corps)) || message || REPLI_GENERIQUE,
    ].join('\n'),
    messageServeur,
  };
}
